import {
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
} from '@mui/material';
import { SalesVsExpensesPageQuery } from '../../../../../../services';
import { useMemo } from 'react';
import { formatFloat, formatLongString } from '../../../../../../helpers';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';

export function ExpensesTable({
    salesVsExpensesQuery,
    showDetails,
    loading = false,
}: {
    salesVsExpensesQuery?: SalesVsExpensesPageQuery;
    showDetails: boolean;
    loading?: boolean;
}) {
    const theme = useTheme();
    const alternateColor = theme.palette.mode === 'dark' ? '#303030' : '#E0E0E0';
    const processedData = useMemo(() => {
        const expenseProductsSummaryByResourceCategory: SalesVsExpensesPageQuery['getExpensesProductsSummaryByResourceCategory']['expenses'] =
            salesVsExpensesQuery?.getExpensesProductsSummaryByResourceCategory
                .expenses || [];

        const expenseProductsSummaryByAll: SalesVsExpensesPageQuery['getExpensesProductsSummaryByAll']['expenses'] =
            salesVsExpensesQuery?.getExpensesProductsSummaryByAll.expenses ||
            [];

        return expenseProductsSummaryByResourceCategory
            .map((expenseByResourceCategory) => {
                return {
                    ...expenseByResourceCategory,
                    children: expenseProductsSummaryByAll
                        .filter((expenseByAll) => {
                            return (
                                expenseByAll.resource_category_id ===
                                expenseByResourceCategory.resource_category_id
                            );
                        })
                        .sort((a, b) => b.total_with_tax - a.total_with_tax),
                };
            })
            .sort((a, b) => {
                const aVal = a.resource_category_name || '';
                const bVal = b.resource_category_name || '';
                return aVal.localeCompare(bVal);
            });
    }, [salesVsExpensesQuery]);

    return (
        <>
            <Typography sx={{ mb: 2 }}>Gastos</Typography>
            <TableContainer>
                <Table size={'small'} padding={'none'}>
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={2} />
                            <TableCell>Kilos</TableCell>
                            <TableCell>Precio</TableCell>
                            <TableCell align="right">Total</TableCell>
                        </TableRow>
                        {loading && processedData.length === 0
                            ? Array.from({ length: 5 }).map((_, r) => (
                                  <TableRow
                                      key={`skeleton-${r}`}
                                      sx={{
                                          backgroundColor:
                                              r % 2 === 1
                                                  ? alternateColor
                                                  : undefined,
                                      }}
                                  >
                                      {Array.from({ length: 5 }).map(
                                          (__, c) => (
                                              <TableCell key={c} sx={{ px: 1 }}>
                                                  <Skeleton variant="text" />
                                              </TableCell>
                                          ),
                                      )}
                                  </TableRow>
                              ))
                            : processedData.map((expenseByResource, i) => {
                            return (
                                <>
                                    <TableRow
                                        sx={{
                                            backgroundColor:
                                                i % 2 === 1
                                                    ? alternateColor
                                                    : undefined,
                                            px: 1,
                                        }}
                                        key={
                                            expenseByResource.resource_category_id
                                        }
                                    >
                                        <TableCell
                                            sx={{
                                                textAlign: 'left',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {
                                                expenseByResource.resource_category_name
                                            }
                                        </TableCell>
                                        <TableCell />
                                        <TableCell
                                            sx={{
                                                textAlign: 'right',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                            }}
                                        >
                                            {expenseByResource.units_sold !== 0
                                                ? formatFloat(
                                                      expenseByResource.units_sold,
                                                  ) + ' kg'
                                                : ''}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textAlign: 'right',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                            }}
                                        >
                                            {expenseByResource.units_sold !== 0
                                                ? formatFloat(
                                                      expenseByResource.total_with_tax /
                                                          expenseByResource.units_sold,
                                                  )
                                                : ''}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textAlign: 'right',
                                                fontWeight: showDetails
                                                    ? 'bold'
                                                    : undefined,
                                                px: 1,
                                            }}
                                        >
                                            {formatFloat(
                                                expenseByResource.total_with_tax,
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {showDetails
                                        ? expenseByResource.children.map(
                                              (expenseByAll, j) => {
                                                  return (
                                                      <TableRow
                                                          sx={{
                                                              backgroundColor:
                                                                  j % 2 === 1
                                                                      ? alternateColor
                                                                      : undefined,
                                                              px: 1,
                                                          }}
                                                          key={
                                                              expenseByAll.account_id
                                                          }
                                                      >
                                                          <TableCell />
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'left',
                                                                  px: 1,
                                                                  whiteSpace:
                                                                      'nowrap',
                                                              }}
                                                          >
                                                              {formatLongString(
                                                                  expenseByAll.account_name,
                                                              )}
                                                          </TableCell>
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'right',
                                                                  px: 1,
                                                              }}
                                                          >
                                                              {expenseByAll.units_sold !==
                                                              0
                                                                  ? formatFloat(
                                                                        expenseByAll.units_sold,
                                                                    ) + ' kg'
                                                                  : ''}
                                                          </TableCell>
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'right',
                                                                  px: 1,
                                                              }}
                                                          >
                                                              {expenseByAll.units_sold !==
                                                              0
                                                                  ? formatFloat(
                                                                        expenseByAll.total_with_tax /
                                                                            expenseByAll.units_sold,
                                                                    )
                                                                  : ''}
                                                          </TableCell>
                                                          <TableCell
                                                              sx={{
                                                                  textAlign:
                                                                      'right',
                                                                  px: 1,
                                                              }}
                                                          >
                                                              {formatFloat(
                                                                  expenseByAll.total_with_tax,
                                                              )}
                                                          </TableCell>
                                                      </TableRow>
                                                  );
                                              },
                                          )
                                        : null}
                                </>
                            );
                              })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}
